import { CacheProvider, EmotionCache } from '@emotion/react';
import { FC, useEffect } from 'react';
import { Hydrate, QueryClientProvider } from '@tanstack/react-query';
import { useAuth, useDimmedLayer, useUnderMaintenance } from '@/logic/state';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type { AppProps } from 'next/app';
import { CssBaseline } from '@mui/material';
import { LanguageProvider } from '../lang';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import NProgress from 'nprogress';
import { QueryClientHandelErrorHook } from '@/hooks/use-query-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Script from 'next/script';
import TagManager from 'react-gtm-module';
import ThemeConfig from '../theme';
import UnderMaintenance from '../components/under-maintenance';
import createEmotionCache from '../utility/createEmotionCache';
import getConfig from 'next/config';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { Layout } from '@/components/layout/layout';

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

const clientSideEmotionCache = createEmotionCache();

const MyApp: FC<MyAppProps> = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const router = useRouter();
  const { queryClient } = QueryClientHandelErrorHook();
  const { publicRuntimeConfig } = getConfig();
  const underMaintenanceMode = useUnderMaintenance(s => s.underMaintenanceMode, shallow);
  const setDimmedLayer = useDimmedLayer(s => s.setDimmedLayer, shallow);
  const user = useAuth(s => s.user, shallow);

  NProgress.configure({
    showSpinner: false,
  });

  useEffect(() => {
    TagManager.initialize({ gtmId: publicRuntimeConfig?.googleTagManagerId });
  }, [publicRuntimeConfig?.googleTagManagerId, router]);

  // This function will invalidate all queries when the user token changes
  useEffect(() => {
    queryClient.invalidateQueries();
  }, [user?.token]);

  useEffect(() => {
    const handleRouteStart = () => {
      setDimmedLayer(true);
      NProgress.start();
    };
    const handleRouteDone = () => {
      setDimmedLayer(false);
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteDone);
    router.events.on('routeChangeError', handleRouteDone);
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteDone);
      router.events.off('routeChangeError', handleRouteDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events, router.pathname]);

  return (
    <CacheProvider value={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={(pageProps as any).dehydratedState}>
          <LanguageProvider serverMessages={(pageProps as any).intlMessages}>
            <LocalizationProvider
              dateAdapter={AdapterLuxon}
              adapterLocale={router.locale}
            >
              <ThemeConfig>
                <Script
                  src='https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js'
                  async
                  data-initial-token='Your Apple Pay JS token'
                ></Script>

                <Script
                  strategy='afterInteractive'
                  src={`https://www.googletagmanager.com/gtag/js?id=${publicRuntimeConfig.googleAnalyticsId}`}
                />

                <Script
                  id='google-analytics'
                  strategy='afterInteractive'
                  dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${publicRuntimeConfig.googleAnalyticsId}', {
                    page_path: window.location.pathname,
                    });`,
                  }}
                />
                <CssBaseline />

                {underMaintenanceMode?.isActive ? (
                  <UnderMaintenance />
                ) : (
                  <Layout>
                    <Component key={router.asPath} {...pageProps} />
                  </Layout>
                )}
              </ThemeConfig>
            </LocalizationProvider>
          </LanguageProvider>
        </Hydrate>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
      </QueryClientProvider>
    </CacheProvider>
  );
};

export default MyApp;
